import React, { createContext, useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import {
  DocumentNode,
  FetchResult,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';

import { Exchange, EXCHANGES } from '../constants/exchange';
import { isGAUser, State } from '../utils/stateSelectors';

type EdeQueryProviderProps = {
  children: React.ReactNode;
  isGa: boolean;
};

type QueryType =
  | 'UPDATE_APP'
  | 'CREATE_APP'
  | 'ADD_APPLICANT_AS_MEMBER'
  | 'ADD_MEMBER'
  | 'REMOVE_MEMBER'
  | 'UPDATE_MEMBER'
  | 'CANCEL_POLICY'
  | 'DELETE_APPLICATION'
  | 'OPT_OUT_POLICY'
  | 'POST_ELIGIBILITY'
  | 'POST_ID_VERIFY'
  | 'REVOKE_PERMISSION'
  | 'SUBMIT_APPLICATION'
  | 'GET_DOCUMENT'
  | 'GET_ENROLLMENT_STATUS'
  | 'GET_ISSUES'
  | 'GET_LATEST_EDN_NOTICE'
  | 'GET_NOTICES'
  | 'GET_PAYMENT_REDIRECT'
  | 'GET_STATE_REFERENCE'
  | 'GET_SYSTEM_REFERENCE'
  | 'GET_APPLICATION_BY_PROD_APP_ID'
  | 'GET_AGENCY_BY_CODE'
  | 'GRANT_PERMISSION'
  | 'UPDATE_PII'
  | 'SUBMIT_ENROLLMENT';

type QueryNames = Record<QueryType, string>;

export type QueriesContextType = QueryNames | null;

export const QueriesContext = createContext<QueriesContextType | null>(null);

const edeQueries: QueryNames = {
  UPDATE_APP: 'updateEdeApplication',
  CREATE_APP: 'createEdeApplication',
  ADD_APPLICANT_AS_MEMBER: 'addApplicantAsMember',
  ADD_MEMBER: 'addMember',
  REMOVE_MEMBER: 'removeMember',
  UPDATE_MEMBER: 'updateMember',
  CANCEL_POLICY: 'cancelPolicy',
  DELETE_APPLICATION: 'deleteEdeApplication',
  OPT_OUT_POLICY: 'optOutPolicy',
  POST_ELIGIBILITY: 'postEligibility',
  POST_ID_VERIFY: 'postIdVerify',
  REVOKE_PERMISSION: 'revokePermission',
  SUBMIT_APPLICATION: 'submitEdeApplication',
  GET_DOCUMENT: 'getEdeDocument',
  GET_ENROLLMENT_STATUS: 'getEnrollmentStatus',
  GET_ISSUES: 'getIssues',
  GET_LATEST_EDN_NOTICE: 'getLatestEdnNotice',
  GET_NOTICES: 'getNotices',
  GET_PAYMENT_REDIRECT: 'getPaymentRedirect',
  GET_STATE_REFERENCE: 'stateReference',
  GET_SYSTEM_REFERENCE: 'systemReference',
  GET_APPLICATION_BY_PROD_APP_ID: 'getEdeApplicationByProdAppId',
  GET_AGENCY_BY_CODE: 'getAgencyByCode',
  GRANT_PERMISSION: 'grantPermission',
  UPDATE_PII: 'updateEdePii',
  SUBMIT_ENROLLMENT: 'submitEnrollment',
};

const gaQueries: QueryNames = {
  UPDATE_APP: 'updateGaepApplication',
  CREATE_APP: 'createGaepApplication',
  ADD_APPLICANT_AS_MEMBER: 'addGaepApplicantAsMember',
  ADD_MEMBER: 'addGaepMember',
  REMOVE_MEMBER: 'removeGaepMember',
  UPDATE_MEMBER: 'updateGaepMember',
  CANCEL_POLICY: 'cancelGaepPolicy',
  DELETE_APPLICATION: 'deleteGaepApplication',
  OPT_OUT_POLICY: 'optOutGaepPolicy',
  POST_ELIGIBILITY: 'postGaepEligibility',
  POST_ID_VERIFY: 'postGaepIdVerify',
  REVOKE_PERMISSION: 'revokeGaepPermission',
  SUBMIT_APPLICATION: 'submitGaepApplication',
  GET_DOCUMENT: 'getGaepEdeDocument',
  GET_ENROLLMENT_STATUS: 'getGaepEnrollmentStatus',
  GET_ISSUES: 'getGaepIssues',
  GET_LATEST_EDN_NOTICE: 'getGaepLatestEdnNotice',
  GET_NOTICES: 'getGaepNotices',
  GET_PAYMENT_REDIRECT: 'getGaepPaymentRedirect',
  GET_STATE_REFERENCE: 'stateGaepReference',
  GET_SYSTEM_REFERENCE: 'systemGaepReference',
  GET_APPLICATION_BY_PROD_APP_ID: 'getGaepApplicationByProdAppId',
  GET_AGENCY_BY_CODE: 'getAgencyByCode',
  GRANT_PERMISSION: 'grantGaepPermission',
  UPDATE_PII: 'updateGaepPii',
  SUBMIT_ENROLLMENT: 'submitGaepEnrollment',
};

// For use when the context provider is not available/hydrated
export const getEdeQueryName = (queryType: QueryType, exchange: Exchange): string => {
  if (exchange === EXCHANGES.GAA) {
    return gaQueries[queryType];
  }

  return edeQueries[queryType];
};

export const useEdeQueryName = (queryType: QueryType): string | null => {
  const context = useContext(QueriesContext);

  if (typeof window === 'undefined') {
    return edeQueries[queryType];
  }

  if (!context) {
    throw new Error('useEdeQueryName must be used within a EdeQueryProvider');
  }

  return context[queryType];
};

const isNonEdeQuery = (queryName: string) => Object.values(gaQueries).includes(queryName);

const getEquivalentEdeQueryName = (queryName: string) => {
  const queryEntries = Object.entries(gaQueries) as [QueryType, string][];
  const [equivalentQueryKey] = queryEntries.find(([, value]) => value === queryName) || [];

  if (!equivalentQueryKey) {
    throw new Error(`No equivalent EDE query found for ${queryName}`);
  }

  return edeQueries[equivalentQueryKey];
};

const mapQueryResult = (result: FetchResult<any>) => {
  const { data } = result;
  if (!data) return result;

  const responseEntries = Object.entries(data);
  const mappedEntries = responseEntries.map(([key, value]) => {
    if (isNonEdeQuery(key)) {
      return [getEquivalentEdeQueryName(key), value];
    }

    return [key, value];
  });

  return { ...result, data: Object.fromEntries(mappedEntries) };
};

export const useEdeMutation = <TData = any, TVariables = OperationVariables>(
  graphqlNode: DocumentNode,
  options: MutationHookOptions<TData, TVariables>,
) => {
  const [submitMutation, ...useMutationResult] = useMutation(graphqlNode, {
    ...options,
    onCompleted(data) {
      const mappedResult = mapQueryResult({ data });
      options.onCompleted?.(mappedResult.data);
    },
  });

  const submitMutationWrapper = useCallback(
    async (submitOptions: MutationHookOptions<TData, TVariables>) => {
      const result = await submitMutation(submitOptions);
      return mapQueryResult(result);
    },
    [submitMutation],
  );

  return [submitMutationWrapper, ...useMutationResult];
};

export const useEdeQuery = (graphqlNode: DocumentNode, options: QueryHookOptions) => {
  const result = useQuery(graphqlNode, options);
  return mapQueryResult(result);
};

export const useEdeLazyQuery = (graphqlNode: DocumentNode) => {
  const [executeQuery] = useLazyQuery(graphqlNode);

  const queryWrapper = useCallback(
    async (options: QueryHookOptions) => {
      const result = await executeQuery(options);
      return mapQueryResult(result);
    },
    [executeQuery],
  );

  return queryWrapper;
};

const mapStateToProps = (state: State) => ({
  isGa: isGAUser(state),
});

const EdeQueryProvider: React.FC<EdeQueryProviderProps> = ({ children, isGa }) => {
  const queries = isGa ? gaQueries : edeQueries;

  return <QueriesContext.Provider value={queries}>{children}</QueriesContext.Provider>;
};

export default connect(mapStateToProps)(EdeQueryProvider);
